// ============================================================
// ANSICHTSSACHE-AGENT – Seitenkopf
// ============================================================
// EIN Kopf fuer jede Seite, Liste wie Detailblatt. Er ist ZWEI
// Zeilen hoch und bleibt es — vorher waren es drei bis vier, und
// die kosteten auf jedem Bildschirm Platz, den die Liste
// darunter besser gebrauchen kann:
//
//   Rueckweg      eigene Zeile ueber dem Titel      19 + 8 px
//   Vorspann      eigene Zeile ueber dem Titel      16 + 6 px
//   Titel                                               30 px
//   Unterzeile                                       6 + 19 px
//
// Was sich geaendert hat, ist nicht der Inhalt, sondern wo er
// steht:
//
//   DER RUECKWEG steht als Pfeil LINKS vom Titel statt als Zeile
//   darueber. Er ist Navigation und kein Vorspann — links neben
//   dem Titel sagt er dasselbe und kostet keine Zeile. Er ist so
//   hoch wie die Knoepfe rechts, damit die Kopfzeile eine Kante
//   hat.
//
//   DER VORSPANN steht als `meta` RECHTS vom Titel, leise und
//   klein. Als Zeile darueber war er das Erste, was man las,
//   obwohl er das Nebensaechlichste ist — Bereich, Zeitraum,
//   Zaehlung. Neben dem Titel ist er da, wenn man ihn sucht.
//
//   DER TITEL steht auf 20 statt 24 Pixeln. Er bleibt die
//   groesste Schrift der Seite; dominant muss er nicht sein,
//   denn wo man ist, sagen Leiste und Register auch.
//
// Der Titel faengt dadurch auf jeder Seite an derselben Stelle
// an — ausser dort, wo der Rueckpfeil davor steht, und genau da
// ist die Verschiebung die Aussage: das hier ist ein Blatt aus
// einer Liste, nicht die Liste selbst.
// ============================================================

import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { PageTitleMemory } from "./page-title-memory";
import { ReturnLink } from "./return-link";

interface PageHeaderProps {
  /** Text — oder, solange die Seite laedt, der Balken des Geruests. */
  title: React.ReactNode;
  /**
   * Der Weg zurueck in die Liste — als Pfeil links vom Titel.
   * `narrowOnly`: nur auf schmalen Bildern, wo die Liste nicht
   * daneben steht (Notizen im Projekt).
   */
  back?: { href: string; label: string; narrowOnly?: boolean };
  /** Das Bild vor dem Titel — ein Personenkreis, ein Zeichen. Genau 36 Pixel hoch. */
  leading?: React.ReactNode;
  /** Was zum Titel selbst gehoert: Zustand, Kennzeichen. */
  badge?: React.ReactNode;
  /** Der leise Zusatz rechts neben dem Titel — Bereich, Zeitraum, Zaehlung. */
  meta?: React.ReactNode;
  /** Die eine Zeile unter dem Titel. */
  description?: React.ReactNode;
  actions?: React.ReactNode;
  /**
   * Wie die Seite im Rueckpfeil einer spaeteren heisst, wenn der
   * Titel es nicht sagt — das Dashboard begruesst mit „Hallo, …“.
   */
  returnName?: string;
  className?: string;
}

function PageHeader({
  title,
  back,
  leading,
  badge,
  meta,
  description,
  actions,
  returnName,
  className,
}: PageHeaderProps) {
  return (
    // `items-center` statt `items-start`: der Textblock ist jetzt
    // hoechstens zwei Zeilen hoch, und gegen zwei Zeilen
    // ausgerichtet sitzen die Knoepfe rechts mittig richtig — sie
    // gehoeren zur ganzen Seite, nicht zur Titelzeile allein.
    //
    // UNTER `md` darf der Kopf umbrechen: reicht die Breite nicht
    // fuer Titel und Knoepfe nebeneinander, stehen die Knoepfe in
    // einer eigenen Zeile darunter. Vorher blieben sie fest stehen,
    // der Titelblock wurde auf wenige Pixel gestaucht — im
    // Projektkopf stand auf dem Telefon jedes Wort in einer eigenen
    // Zeile, und die Seite lief seitlich aus dem Bild. Ab `md`
    // bleibt es bei einer Reihe, dort gibt der Titel nach.
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-x-4 gap-y-3 md:flex-nowrap",
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        {/* Der Pfeil fuehrt dorthin zurueck, woher man in diesem
            Reiter gekommen ist — ohne Weg davor in die Liste mit
            ihrem letzten Filter (`lib/return-address.ts`). */}
        {back && (
          <ReturnLink
            back={back}
            className={cn(
              // `-ml-2` holt die optische Kante des Pfeils auf die
              // Textkante zurueck: seine Flaeche ist 36 Pixel
              // breit, das Zeichen darin 16.
              "-ml-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
              "text-text-tertiary hover:bg-bg-tertiary hover:text-text-primary",
              "duration-fast ease-standard transition-colors",
              back.narrowOnly && "lg:hidden",
            )}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
          </ReturnLink>
        )}

        {leading && <div className="shrink-0">{leading}</div>}

        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-x-2.5">
            {/* Der Titel gibt als LETZTER nach. Er stand hier mit
                `truncate` neben einem Zusatz mit `shrink-0` — und
                weil nur er nachgeben konnte, wurde aus „AN-26057“
                neben einem langen Kundennamen ein „AN…“, waehrend
                der Zusatz vollstaendig dastand. Die beiden
                Gewichte drehen das um: der Zusatz nimmt die ganze
                Stauchung auf, der Titel bleibt, solange es geht. */}
            <h2 className="text-heading-md text-text-primary shrink-[1] truncate">{title}</h2>
            {badge}
            {meta && (
              // Unter `md` faellt der Zusatz ganz weg statt den
              // Titel abzuschneiden: der Titel ist die Aussage,
              // der Zusatz die Fussnote.
              <span className="text-caption text-text-tertiary hidden min-w-0 shrink-[999] truncate md:inline">
                {meta}
              </span>
            )}
          </div>

          {/* Merkt sich den Titel, damit der Pfeil eines spaeteren
              Blattes sagen kann, wohin er zurueckfuehrt. */}
          <PageTitleMemory name={returnName} />

          {/* Ein `div`, kein `p`: im Geruest steht hier ein Balken
              (`Skeleton` ist ein `div`), und ein `div` im `p` bricht
              das Hydrieren. */}
          {description && (
            <div className="text-body-sm text-text-secondary mt-1 max-w-2xl">{description}</div>
          )}
        </div>
      </div>

      {/* Auf dem Telefon bleibt die Knopfreihe EINE Zeile und rollt
          seitlich. Umgebrochen standen dort bis zu drei Reihen
          Knoepfe ueber der Liste (Rechnungen: fuenf Knoepfe, 90
          Pixel). `py-1`/`-my-1` lassen den Fokusring stehen, den der
          Rollbereich sonst abschnitte; die Aufklapper der Knoepfe
          haengen an <body> und werden nicht beschnitten. */}
      {actions && (
        <div
          className={cn(
            "flex min-w-0 items-center gap-2 md:shrink-0",
            "max-md:-my-1 max-md:[scrollbar-width:none] max-md:overflow-x-auto max-md:overscroll-x-contain max-md:py-1 max-md:[&::-webkit-scrollbar]:hidden max-md:[&>*]:shrink-0",
          )}
        >
          {actions}
        </div>
      )}
    </div>
  );
}

export { PageHeader };
